import React, { useState, useEffect } from 'react';
import { getApps, cancelAppSubmission, getAppReviewSubmission } from '../../services/api';

function AppSelector({ onSelectApp }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hovered, setHovered] = useState(null);
  const [cancelInfo, setCancelInfo] = useState(null);
  const [showCancelDetails, setShowCancelDetails] = useState(false);
  const [rejectionInfo, setRejectionInfo] = useState(null);
  const [showRejectionDetails, setShowRejectionDetails] = useState(false);
  const [loadingRejection, setLoadingRejection] = useState(false);

  // Format app status for display
  const formatAppStatus = (status) => {
    if (!status) return 'Unknown';
    
    const statusMap = {
      // App states
      'READY_FOR_SALE': 'Ready for Sale',
      // Version states
      'DEVELOPER_REJECTED': 'Developer Rejected',
      'REJECTED': 'Rejected',
      'METADATA_REJECTED': 'Metadata Rejected',
      'WAITING_FOR_REVIEW': 'Waiting for Review',
      'IN_REVIEW': 'In Review',
      'PENDING_RELEASE': 'Pending Release',
      'PROCESSING_FOR_DISTRIBUTION': 'Processing for Distribution',
      'PREPARE_FOR_SUBMISSION': 'Prepare for Submission',
      'INVALID_BINARY': 'Invalid Binary',
      'REMOVED_FROM_SALE': 'Removed from Sale'
    };
    
    return statusMap[status] || status;
  };

  // Add this helper for status color
  const getStatusStyle = (status) => {
    const baseStyle = { fontWeight: 500, borderRadius: '9999px', padding: '4px 12px', display: 'inline-block', fontSize: '12px' };
    switch (status) {
      case 'READY_FOR_SALE':
        return { ...baseStyle, color: 'var(--success)', background: 'var(--success-light)' };
      case 'IN_REVIEW':
        return { ...baseStyle, color: 'var(--primary)', background: 'var(--primary-light)' };
      case 'WAITING_FOR_REVIEW':
        return { ...baseStyle, color: 'var(--warning)', background: 'var(--warning-light)' };
      case 'REJECTED':
      case 'METADATA_REJECTED':
      case 'DEVELOPER_REJECTED':
        return { ...baseStyle, color: 'var(--error)', background: 'var(--error-light)' };
      case 'PENDING_RELEASE':
        return { ...baseStyle, color: 'var(--primary)', background: 'var(--primary-light)' };
      case 'PROCESSING_FOR_DISTRIBUTION':
        return { ...baseStyle, color: 'var(--primary)', background: 'var(--primary-light)' };
      case 'REMOVED_FROM_SALE':
        return { ...baseStyle, color: 'var(--text-tertiary)', background: 'var(--bg-tertiary)' };
      default:
        return { ...baseStyle, color: 'var(--text-tertiary)', background: 'var(--bg-tertiary)' };
    }
  };

  // Helper to get App Store URL
  const getAppStoreUrl = (appId) => `https://apps.apple.com/app/id${appId}`;

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getApps();
      if (response.data.success) {
        setApps(response.data.apps);
      } else {
        const errorMsg = typeof response.data.error === 'object' 
          ? response.data.error.message || 'Failed to load apps'
          : response.data.error || 'Failed to load apps';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to load apps'
        : err.response?.data?.error || err.message || 'Failed to load apps';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const filteredApps = apps.filter(app =>
    app.attributes.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.attributes.bundleId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper to check if app can have submission cancelled
  const canCancelSubmission = (status) => {
    return ['IN_REVIEW', 'WAITING_FOR_REVIEW'].includes(status);
  };

  // Helper to check if app is rejected and can show rejection reasons
  const isRejectedStatus = (status) => {
    return ['REJECTED', 'METADATA_REJECTED', 'DEVELOPER_REJECTED'].includes(status);
  };

  // Handle checking rejection reasons
  const handleCheckRejection = async (app, event) => {
    event.stopPropagation(); // Prevent triggering the app selection
    
    setLoadingRejection(true);
    setRejectionInfo(null);
    setShowRejectionDetails(true);

    try {
      // First get the app's versions to find the rejected version
      const versionsResponse = await fetch(`/api/apps/${app.id}/versions`, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!versionsResponse.ok) {
        throw new Error('Failed to get app versions');
      }

      const versionsData = await versionsResponse.json();
      
      if (!versionsData.success || !versionsData.versions || versionsData.versions.length === 0) {
        setRejectionInfo({
          type: 'error',
          message: 'No versions found for this app'
        });
        setLoadingRejection(false);
        return;
      }

      // Find the most recent rejected version
      const rejectedVersion = versionsData.versions.find(v => 
        ['REJECTED', 'METADATA_REJECTED', 'DEVELOPER_REJECTED'].includes(v.attributes?.appStoreState)
      );

      if (!rejectedVersion) {
        setRejectionInfo({
          type: 'warning',
          message: 'App shows as rejected but could not find the rejected version'
        });
        setLoadingRejection(false);
        return;
      }

      console.log('Checking rejection for version:', rejectedVersion.id);

      // Get the rejection details
      const result = await getAppReviewSubmission(rejectedVersion.id);
      
      console.log('Rejection check result:', result.data);
      
      if (result.data.success && result.data.hasSubmission) {
        const submission = result.data.submission;
        
        if (submission.isRejected && submission.rejectionDetails) {
          setRejectionInfo({
            type: 'rejection',
            message: submission.rejectionDetails.message,
            details: submission.rejectionDetails,
            versionString: rejectedVersion.attributes?.versionString,
            state: submission.state
          });
        } else if (submission.isRejected) {
          // Rejected but no detailed rejection info available
          setRejectionInfo({
            type: 'rejection',
            message: 'This version was rejected by Apple Review.',
            details: {
              detailsLocation: 'Resolution Center in App Store Connect',
              instructions: [
                '1. Go to https://appstoreconnect.apple.com',
                '2. Select your app',
                '3. Go to the rejected version',
                '4. Look for "Resolution Center" or rejection messages',
                '5. Read the rejection reasons and reviewer comments',
                '6. Make the necessary changes',
                '7. Resubmit when ready'
              ]
            },
            versionString: rejectedVersion.attributes?.versionString,
            state: submission.state
          });
        } else {
          setRejectionInfo({
            type: 'info',
            message: 'Version found but not currently in rejected state',
            details: { state: submission.state }
          });
        }
      } else if (result.data.success && !result.data.hasSubmission) {
        // No submission found but version is rejected - likely rejected through web UI
        setRejectionInfo({
          type: 'rejection',
          message: 'This version is in rejected state.',
          details: {
            detailsLocation: 'Resolution Center in App Store Connect',
            instructions: [
              '1. Go to https://appstoreconnect.apple.com',
              '2. Select your app',
              '3. Go to the rejected version',
              '4. Look for "Resolution Center" or rejection messages in the version page',
              '5. Read the rejection reasons and reviewer comments',
              '6. Make the necessary changes',
              '7. Resubmit when ready'
            ]
          },
          versionString: rejectedVersion.attributes?.versionString,
          note: result.data.message
        });
      } else {
        setRejectionInfo({
          type: 'warning',
          message: result.data.error || result.data.message || 'Could not retrieve rejection information',
          details: {
            note: 'Rejection details may only be available in App Store Connect',
            instructions: [
              '1. Go to https://appstoreconnect.apple.com',
              '2. Select your app',
              '3. Find the rejected version',
              '4. Check Resolution Center for rejection details'
            ]
          }
        });
      }
    } catch (error) {
      console.error('Error checking rejection:', error);
      setRejectionInfo({
        type: 'error',
        message: 'Failed to get rejection information: ' + error.message,
        details: {
          note: 'You can still view rejection reasons in App Store Connect',
          instructions: [
            '1. Go to https://appstoreconnect.apple.com',
            '2. Select your app',
            '3. Check the Resolution Center for rejection details'
          ]
        }
      });
    } finally {
      setLoadingRejection(false);
    }
  };

  // Handle cancel submission
  const handleCancelSubmission = async (app, event) => {
    event.stopPropagation(); // Prevent triggering the app selection
    
    const confirmMessage = `Are you sure you want to cancel the submission for "${app.attributes.name}"?\n\n` +
      `This will attempt to remove the app from Apple's review queue.\n\n` +
      `Note: If the submission has already been processed by Apple, you may need to use "Developer Rejected" in App Store Connect instead.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      console.log('Cancelling submission for app:', app.id);
      const result = await cancelAppSubmission(app.id);
      
      console.log('Cancel submission result:', result.data);
      
      if (result.data.success) {
        const message = result.data.message || 'Submission cancelled successfully!';
        const details = result.data.details?.successfulDeletes 
          ? `\n\nDeleted ${result.data.details.successfulDeletes} review submission item(s).`
          : '';
        // show success message briefly in the UI
        setCancelInfo({ type: 'success', message: `${message}${details}`, raw: result.data });
        setShowCancelDetails(false);
        // Refresh the apps list to show updated status
        loadApps();
      } else {
        // Handle specific error cases
        const error = result.data.error || 'Unknown error';
        const suggestion = result.data.suggestion || '';
        const message = result.data.message || '';
        // detect common API state that means user must reject via App Store Connect
        let needsManualReject = false;
        try {
          const details = result.data.details || {};
          if (details.deleteResults && Array.isArray(details.deleteResults)) {
            for (const d of details.deleteResults) {
              const err = d.error;
              if (err && err.details) {
                for (const ed of err.details) {
                  if (ed.code && ed.code.includes('STATE_ERROR.ENTITY_STATE_INVALID')) {
                    needsManualReject = true;
                    break;
                  }
                }
              }
            }
          }
        } catch (e) {
          // ignore parsing errors
        }

        const friendlyMessage = needsManualReject
          ? 'This submission cannot be cancelled via API because Apple has already processed the submission. Please use App Store Connect to reject the binary (Developer Reject) or contact Apple Support.'
          : (message || 'Failed to cancel submission via API');

        setCancelInfo({
          type: 'error',
          message: friendlyMessage,
          details: { error, suggestion, raw: result.data }
        });
        setShowCancelDetails(true);
      }
    } catch (error) {
      console.error('Cancel submission error:', error);
      const errorPayload = error.response?.data || { message: error.message };
      setCancelInfo({ type: 'error', message: 'Error cancelling submission', details: { raw: errorPayload } });
      setShowCancelDetails(true);
    }
  };

  // Add button style helper
  const getButtonStyle = (type) => {
    if (type === 'manage') {
      return {
        background: 'var(--primary)',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontWeight: 'bold',
        padding: '8px 18px',
        marginRight: 8,
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        transition: 'background 0.2s',
      };
    }
    if (type === 'view') {
      return {
        background: 'var(--bg-secondary)',
        color: 'var(--primary)',
        border: '1px solid var(--border-light)',
        borderRadius: '6px',
        fontWeight: 'bold',
        padding: '8px 18px',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'pointer',
        transition: 'background 0.2s',
      };
    }
    return {};
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span className="alert-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  // Add styles for the app selector
  const styles = {
    card: {
      background: 'var(--bg-primary)',
      borderRadius: 16,
      boxShadow: 'var(--shadow-lg)',
      padding: '2.5rem 2rem',
      maxWidth: 600,
      //margin: '2rem auto',
      border: '1px solid var(--border-light)',
    },
    cardHeader: {
      fontSize: '1.5rem',
      fontWeight: 700,
      marginBottom: 24,
      color: 'var(--text-primary)',
      letterSpacing: 0.5,
      textAlign: 'center',
    },
    formGroup: {
      marginBottom: 24,
      textAlign: 'center',
    },
    formInput: {
      width: '100%',
      maxWidth: 350,
      padding: '12px 16px',
      borderRadius: 8,
      border: '1px solid var(--border-light)',
      fontSize: '1rem',
      outline: 'none',
      boxShadow: 'var(--shadow-sm)',
      margin: '0 auto',
      display: 'block',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
    },
    appList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      marginTop: 8,
    },
    appItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'var(--bg-secondary)',
      borderRadius: 12,
      padding: '1.5rem 1.5rem',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--border-light)',
      transition: 'box-shadow 0.2s, border 0.2s',
      cursor: 'pointer',
    },
    appItemHover: {
      boxShadow: 'var(--shadow-lg)',
      border: '1.5px solid var(--primary)',
    },
    appInfo: {
      flex: 1,
      minWidth: 0,
    },
    appName: {
      fontSize: '1.15rem',
      fontWeight: 600,
      color: 'var(--text-primary)',
      marginBottom: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    bundleId: {
      color: 'var(--text-secondary)',
      fontSize: '0.98rem',
      marginBottom: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    sku: {
      fontSize: '0.85rem',
      color: 'var(--text-tertiary)',
      marginBottom: 6,
    },
    status: {
      marginTop: 8,
    },
    buttonGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      minWidth: 150,
      alignItems: 'flex-end',
    },
  };

  return (
    <div style={styles.card}>
      {rejectionInfo && showRejectionDetails && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            padding: '12px 16px',
            borderRadius: 8,
            border: rejectionInfo.type === 'rejection' ? '1px solid var(--error)' : '1px solid var(--primary)',
            background: rejectionInfo.type === 'rejection' ? 'var(--error-light)' : 'var(--primary-light)',
            color: 'var(--text-primary)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <strong>
                  {rejectionInfo.type === 'rejection' ? '❌ App Rejection Details' : 'ℹ️ Information'}
                </strong>
                <div style={{ marginTop: 6 }}>
                  {rejectionInfo.versionString && (
                    <div style={{ fontSize: '0.9rem', marginBottom: 4 }}>
                      <strong>Version:</strong> {rejectionInfo.versionString}
                    </div>
                  )}
                  <div>{rejectionInfo.message}</div>
                  
                  {rejectionInfo.note && (
                    <div style={{ marginTop: 6, fontSize: '0.85rem', fontStyle: 'italic', opacity: 0.8 }}>
                      {rejectionInfo.note}
                    </div>
                  )}
                  
                  {rejectionInfo.state && (
                    <div style={{ marginTop: 6, fontSize: '0.85rem' }}>
                      <strong>Status:</strong> {rejectionInfo.state}
                    </div>
                  )}
                </div>
                
                {rejectionInfo.details && rejectionInfo.details.instructions && (
                  <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.5)', borderRadius: 6 }}>
                    <strong>To view detailed rejection reasons:</strong>
                    <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                      {rejectionInfo.details.instructions.map((instruction, idx) => (
                        <li key={idx} style={{ marginBottom: 4 }}>{instruction}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {rejectionInfo.details && rejectionInfo.details.detailsLocation && (
                  <div style={{ marginTop: 8, fontSize: '0.9rem', fontStyle: 'italic' }}>
                    Detailed rejection messages are available in: <strong>{rejectionInfo.details.detailsLocation}</strong>
                  </div>
                )}
              </div>
              <button 
                onClick={() => { setRejectionInfo(null); setShowRejectionDetails(false); }} 
                style={{ 
                  cursor: 'pointer', 
                  marginLeft: 12,
                  padding: '4px 8px',
                  background: 'rgba(0,0,0,0.1)',
                  border: 'none',
                  borderRadius: 4
                }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
      
      {cancelInfo && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            padding: '12px 16px',
            borderRadius: 8,
            border: cancelInfo.type === 'success' ? '1px solid var(--success)' : '1px solid var(--error)',
            background: cancelInfo.type === 'success' ? 'var(--success-light)' : 'var(--error-light)',
            color: 'var(--text-primary)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ maxWidth: '85%' }}>
              <strong>{cancelInfo.type === 'success' ? 'Success' : 'Action required'}</strong>
              <div style={{ marginTop: 6 }}>{cancelInfo.message}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => setShowCancelDetails(s => !s)} style={{ cursor: 'pointer' }}>
                {showCancelDetails ? 'Hide details' : 'Show details'}
              </button>
              <button onClick={() => { setCancelInfo(null); setShowCancelDetails(false); }} style={{ cursor: 'pointer' }}>
                Dismiss
              </button>
            </div>
          </div>
          {showCancelDetails && cancelInfo.details && (
            <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8, padding: 12, background: 'var(--bg-secondary)', borderRadius: 6, border: '1px solid var(--border-light)', maxHeight: 260, overflow: 'auto', color: 'var(--text-primary)' }}>
              {JSON.stringify(cancelInfo.details.raw || cancelInfo.details || cancelInfo, null, 2)}
            </pre>
          )}
        </div>
      )}
      <div style={styles.cardHeader}>Select an App</div>
      <div style={styles.formGroup}>
        <input
          type="text"
          style={styles.formInput}
          placeholder="Search apps by name or bundle ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div style={styles.appList}>
        {filteredApps.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
            No apps found
          </p>
        ) : (
          filteredApps.map(app => (
            <div
              key={app.id}
              style={{
                ...styles.appItem,
                ...(hovered === app.id ? styles.appItemHover : {}),
              }}
              onMouseEnter={() => setHovered(app.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelectApp(app)}
            >
              <div style={styles.appInfo}>
                <div style={styles.appName}>{app.attributes.name}</div>
                <div style={styles.bundleId}>{app.attributes.bundleId}</div>
                <div style={styles.sku}>SKU: {app.attributes.sku}</div>
                <div style={styles.status}>
                  <span style={getStatusStyle(app.attributes.currentVersionState)}>
                    Status: {formatAppStatus(app.attributes.currentVersionState)}
                  </span>
                </div>
              </div>
              <div style={styles.buttonGroup} onClick={e => e.stopPropagation()}>
                <button
                  style={getButtonStyle('manage')}
                  onClick={() => onSelectApp(app)}
                >
                  Manage →
                </button>
                <button
                  style={getButtonStyle('view')}
                  onClick={() => window.open(getAppStoreUrl(app.id), '_blank')}
                >
                  View in App Store
                </button>
                {isRejectedStatus(app.attributes.currentVersionState) && (
                  <button
                    style={{
                      background: 'var(--warning)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      padding: '8px 18px',
                      boxShadow: 'var(--shadow-sm)',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onClick={(e) => handleCheckRejection(app, e)}
                    disabled={loadingRejection}
                    title="View rejection reasons and what to fix"
                  >
                    {loadingRejection ? '⏳ Loading...' : '🔍 Why Rejected?'}
                  </button>
                )}
                {canCancelSubmission(app.attributes.currentVersionState) && (
                  <button
                    style={{
                      background: 'var(--error)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      padding: '8px 18px',
                      boxShadow: 'var(--shadow-sm)',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                    }}
                    onClick={(e) => handleCancelSubmission(app, e)}
                    title="Cancel the current submission for review"
                  >
                    ❌ Cancel Submission
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AppSelector;
